import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Tabs, Tab, Skeleton, Stack } from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import GetNiveles from "../../data/GetNiveles";
import GetAsignaturas from "../../data/GetAsignaturas";
import GetObjetivosAprendizaje from "../../components/ObjetivosAprendizaje/GetObjetivosAprendizaje";
import GetEspecialidad from "../../data/GetEspecialidad";
import GetObjetivosAprendizajeV2 from "../ObjetivosAprendizajeTP/GetObjetivosAprendizajeV2";
import GetNivelesTP from "../../data/GetNivelesTP";
import type { FormattedNivelTP } from "../../data/GetNivelesTP";
import GetAsignaturasTP from "../../data/GetAsignaturasTP";
import ConfirmTabChangeModal, { type ClearMode } from "./ConfirmTabChangeModal";
import { useOAS } from "../../context/SuperAdmin/OASContexts/OASContext";
import GetNivelesOrganizacion from "../../data/GetNivelesOrganizacion";
import GetAsignaturasOrganizacion from "../../data/GetAsignaturasOrganizacion";
import GetObjetivosAprendizajeOrganizacion from "../ObjetivosAprendizaje/GetObjetivosAprendizajeOrganizacion";
import { useProyecto } from "../../data/ProyectoContext";


type GetNivelesProps = React.ComponentProps<typeof GetNiveles>;
type GetAsignaturasProps = React.ComponentProps<typeof GetAsignaturas>;
type GetObjetivosAprendizajeProps =
  React.ComponentProps<typeof GetObjetivosAprendizaje>;

interface CurriculumTabsProps {
  nivelId: GetNivelesProps["selectedNivel"];
  materiaId: GetAsignaturasProps["selectedAsignatura"];
  handleNivelChange: GetNivelesProps["onNivelChange"];

  nivelTPId: string;
  asignaturaTPId: string;
  handleNivelTPChange: (nivel_tp: FormattedNivelTP) => void;
  handleAsignaturaTPChangeLocal: (a: { id: string; nombre: string }) => void;
  clearTP: () => void;

  handleAsignaturaChangeLocal: GetAsignaturasProps["onAsignaturaChange"];

  promptData: {
    materia: GetObjetivosAprendizajeProps["nombreAsignatura"];
    nivel: GetObjetivosAprendizajeProps["nombreNivel"];
    [key: string]: any;
  };
  setPromptData: React.Dispatch<React.SetStateAction<any>>;

   onTabValueChange?: (tab: TabIndex) => void;
}

export enum TabIndex {
  CURRICULUM_NACIONAL = 0,
  OBJETIVOS_ORGANIZACION = 1,
  TECNICO_PROFESIONAL = 2,
  OBJETIVOS_PROPIOS = 3,
}

const BORDER_COLOR = "#D3D3D3";
const EMPTY_ID_NOMBRE = { id: "", nombre: "" };

function getId(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "id" in value) {
    return String((value as any).id ?? "");
  }
  return "";
}

const CurriculumTabs: React.FC<CurriculumTabsProps> = ({
  nivelId,
  materiaId,
  handleNivelChange,
  nivelTPId,
  asignaturaTPId,
  handleNivelTPChange,
  handleAsignaturaChangeLocal,
  handleAsignaturaTPChangeLocal,
  clearTP,
  promptData,
  setPromptData,
  onTabValueChange
}) => {
  const [tabValue, setTabValue] = useState<TabIndex>(
    TabIndex.CURRICULUM_NACIONAL
  );

  const [clearMode, setClearMode] = useState<ClearMode>("TP");
  const [clearFromTab, setClearFromTab] = useState<TabIndex | null>(null);

  const [especialidad, setEspecialidad] = useState<{
    id: string;
    nombre: string;
  } | null>(null);

  const [nivelOrg, setNivelOrg] = useState<{ id: string; nombre: string }>(
    EMPTY_ID_NOMBRE
  );
  const [asignaturaOrg, setAsignaturaOrg] = useState<{ id: string; nombre: string }>(
    EMPTY_ID_NOMBRE
  );

  const { setProyecto } = useProyecto();


  const [confirmTabChangeOpen, setConfirmTabChangeOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabIndex | null>(null);

  const isTecnicoProfesional = tabValue === TabIndex.TECNICO_PROFESIONAL;
  const isOrganizacion = tabValue === TabIndex.OBJETIVOS_ORGANIZACION;

  const especialidadId = especialidad?.id ?? "";


  const hasAnyTPSel = Boolean(especialidadId || nivelTPId || asignaturaTPId);


  const nivelCNId = useMemo(() => getId(nivelId), [nivelId]);
  const materiaCNId = useMemo(() => getId(materiaId), [materiaId]);
  const hasAnyCNSel = Boolean(nivelCNId || materiaCNId);


  const hasAnyOrgSel = Boolean(nivelOrg.id || asignaturaOrg.id);

  const hasAnyNonTPSel = useMemo(() => {
    return hasAnyCNSel || hasAnyOrgSel;
  }, [hasAnyCNSel, hasAnyOrgSel]);

  const clearNonTP_All = () => {
    clearNonTP_CN();
    clearNonTP_ORG();
  };

  //Valida el cambio de la tab
  useEffect(() => {
  onTabValueChange?.(tabValue);
}, [tabValue, onTabValueChange]);


  const clearNonTP_CN = () => {
    handleAsignaturaChangeLocal(EMPTY_ID_NOMBRE as any);
    handleNivelChange("", "" as any);
  };

  const clearNonTP_ORG = () => {
    setNivelOrg(EMPTY_ID_NOMBRE);
    setAsignaturaOrg(EMPTY_ID_NOMBRE);

    // Se limpia promptData al salir
    setPromptData((prev: any) => ({
      ...prev,
      nivel: "",
      materia: "",
    }));
  };

  useEffect(() => {
    console.log("Se activa limpieza:", clearFromTab);

  }, [clearFromTab]);


 const { allowedTP, isLoadingAllowedTP } = useOAS();
const isLoadingTPInfo = isLoadingAllowedTP;

  useEffect(() => {
  if (!allowedTP && tabValue === TabIndex.TECNICO_PROFESIONAL) {
    setEspecialidad(null);
    clearTP();
    setTabValue(TabIndex.CURRICULUM_NACIONAL);
  }
}, [allowedTP, tabValue, clearTP]);

  useEffect(() => {
    if (!allowedTP && tabValue === TabIndex.TECNICO_PROFESIONAL) {
      setEspecialidad(null);
      clearTP();
      setTabValue(TabIndex.CURRICULUM_NACIONAL);
    }
  }, [allowedTP, tabValue, clearTP]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: TabIndex) => {
    const nextTab = newValue;

    // No permite entrar a TP si no está habilitado
    if (!allowedTP && nextTab === TabIndex.TECNICO_PROFESIONAL) return;

   
    if (
      tabValue === TabIndex.TECNICO_PROFESIONAL &&
      nextTab !== TabIndex.TECNICO_PROFESIONAL &&
      hasAnyTPSel
    ) {
      setClearMode("TP");
      setPendingTab(nextTab);
      setConfirmTabChangeOpen(true);
      return;
    }

    
 
    if (
      tabValue !== TabIndex.TECNICO_PROFESIONAL &&
      nextTab === TabIndex.TECNICO_PROFESIONAL &&
      hasAnyNonTPSel
    ) {
      setClearMode("NON_TP");
      setPendingTab(nextTab);
      setConfirmTabChangeOpen(true);
      return;
    }

    
    setTabValue(nextTab);

    
    if (
      tabValue === TabIndex.TECNICO_PROFESIONAL &&
      nextTab !== TabIndex.TECNICO_PROFESIONAL
    ) {
      setEspecialidad(null);
      clearTP();
    }
  };


  const handleConfirmTabChange = () => {
    if (clearMode === "TP") {
      setEspecialidad(null);
      clearTP();
    } else {
     
      clearNonTP_All();
    }

    if (pendingTab !== null) setTabValue(pendingTab);
    setPendingTab(null);
    setClearFromTab(null);
    setConfirmTabChangeOpen(false);
  };

  const handleCancelTabChange = () => {
    setPendingTab(null);
    setClearFromTab(null);
    setConfirmTabChangeOpen(false);
  };

  const tabSx = (isActive: boolean) => ({
    textTransform: "none",
    fontFamily: "Montserrat, Arial, sans-serif",
    fontWeight: isActive ? 700 : 500,
    fontSize: "0.95rem",
    minHeight: 0,
    py: 1.2,
    px: 2.5,
    mr: 0.4,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    border: isActive ? `1px solid ${BORDER_COLOR}` : "1px solid transparent",
    borderBottom: isActive ? "1px solid #FFFFFF" : "1px solid transparent",
    bgcolor: isActive ? "#FFFFFF" : "transparent",
    color: "#1D3D4D",
    "&:hover": {
      bgcolor: isActive ? "#FFFFFF" : "rgba(144,175,197,0.12)",
    },
  });

  return (
    <Box sx={{ width: "100%", borderRadius: 2, bgcolor: "#FFFFFF", }}>
      <ConfirmTabChangeModal
        open={confirmTabChangeOpen}
        clearMode={clearMode}
        onConfirm={handleConfirmTabChange}
        onCancel={handleCancelTabChange}
      />

      {/* TABS */}
      <Tabs
        value={tabValue}
        onChange={handleChangeTab}
        aria-label="Tabs currículum"
        sx={{
          borderBottom: `1px solid ${BORDER_COLOR}`,
          minHeight: 0,
          "& .MuiTabs-flexContainer": {
            alignItems: "flex-end",
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        <Tab
          value={TabIndex.CURRICULUM_NACIONAL}
          label="Currículum Nacional"
          disableRipple
          sx={() => tabSx(tabValue === TabIndex.CURRICULUM_NACIONAL)}
        />

        {/* Objetivos de organización */}
        <Tab
          value={TabIndex.OBJETIVOS_ORGANIZACION}
          label="Objetivos de organización"
          disableRipple
          sx={() => tabSx(tabValue === TabIndex.OBJETIVOS_ORGANIZACION)}
        />

        {/* TP */}
        {isLoadingTPInfo ? (
          <Tab
            value={TabIndex.TECNICO_PROFESIONAL}
            disabled
            icon={
              <Skeleton
                variant="rounded"
                width={140}
                height={18}
                sx={{ borderRadius: 999 }}
              />
            }
            iconPosition="start"
            label=""
            disableRipple
            sx={{
              minHeight: 0,
              py: 1.2,
              px: 2.5,
              mr: 0.4,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              color: "#1D3D4D",
              opacity: 1,
              "&.Mui-disabled": { opacity: 1 },
            }}
          />
        ) : (
          allowedTP && (
            <Tab
              value={TabIndex.TECNICO_PROFESIONAL}
              label="Técnico Profesional"
              disableRipple
              sx={() => tabSx(tabValue === TabIndex.TECNICO_PROFESIONAL)}
            />
          )
        )}
      </Tabs>

      <Box
        sx={{
          p: 3,
          border: `1px solid ${BORDER_COLOR}`,
          borderTop: "none",
          borderRadius: "0 0 16px 16px",
          overflow: "hidden",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={tabValue}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <Grid container spacing={2}>
              {isTecnicoProfesional && isLoadingTPInfo ? (
                <>
                  <Grid item xs={12} md={4}>
                    <Skeleton variant="rounded" height={56} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Skeleton variant="rounded" height={56} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Skeleton variant="rounded" height={56} />
                  </Grid>
                  <Grid item xs={12} mt={3}>
                    <Stack spacing={1.2}>
                      <Skeleton variant="rounded" height={22} />
                      <Skeleton variant="rounded" height={22} />
                      <Skeleton variant="rounded" height={22} />
                      <Skeleton variant="rounded" height={22} width="85%" />
                    </Stack>
                  </Grid>
                </>
              ) : (
                <>

                  {/* Especialidad (solo TP) */}
                  {isTecnicoProfesional && (
                    <Grid item xs={12} md={4}>
                      <GetEspecialidad
                        selectedEspecialidadId={especialidadId}
                        onEspecialidadChange={(esp) => {
                          setEspecialidad(esp);
                          clearTP();
                        }}
                      />
                    </Grid>
                  )}

                  {/* NIVEL */}
                  {isTecnicoProfesional ? (
                    <Grid item xs={12} md={4}>
                      <GetNivelesTP
                        especialidadId={especialidadId}
                        selectedNivelId={nivelTPId}
                        onNivelChange={(nivel) => {
                          handleNivelTPChange(nivel);
                          setPromptData((prev: any) => ({
                            ...prev,
                            nivel_tp: nivel?.nombre ?? "",
                            nivel_cn: "",
                            nivel_org: ""
                          }));
                          setProyecto((prev: any) => ({
                            ...prev,
                            nivel_curso_tp: nivel?.nombre ?? "",
                            nivel_curso_cn: "",
                            nivel_curso_org: ""
                          }));
                        }}
                      />
                    </Grid>
                  ) : isOrganizacion ? (
                    <Grid item xs={12} md={6}>
                      <GetNivelesOrganizacion
                        selectedNivel={nivelOrg.id as any}
                        onNivelChange={(id: any, nombre: any) => {
                          const next = {
                            id: String(id ?? ""),
                            nombre: String(nombre ?? ""),
                          };
                          setNivelOrg(next);
                          setAsignaturaOrg(EMPTY_ID_NOMBRE); // reset asignatura al cambiar nivel
                          setPromptData((prev: any) => ({
                            ...prev,
                            nivel_org: next.nombre,
                            nivel_tp: "",
                            nivel_cn: prev.nivel_cn || "",
                          }));
                          setProyecto((prev: any) => ({
                            ...prev,
                            nivel_curso_org: next.nombre,
                            nivel_curso_tp: "",
                            nivel_curso_cn: prev.nivel_curso_cn || "",
                          }));
                        }}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <GetNiveles
                        onNivelChange={handleNivelChange}
                        selectedNivel={nivelId}
                      />
                    </Grid>
                  )}

                  {/* ASIGNATURA */}
                  {isTecnicoProfesional ? (
                    <Grid item xs={12} md={4}>
                      <GetAsignaturasTP
                        especialidadId={especialidadId}
                        nivelId={nivelTPId}
                        selectedAsignaturaId={asignaturaTPId}
                        onAsignaturaChange={(a) => {
                          handleAsignaturaTPChangeLocal(a);
                          setPromptData((prev: any) => ({
                            ...prev,
                            materia_tp: a?.nombre ?? "",
                            materia_cn: "",
                            materia_org: ""
                          }));
                          setProyecto((prev: any) => ({
                            ...prev,
                            materias_cn: [],
                            materias_org: [],
                            materias_tp: [a.nombre]
                          }));
                        }}
                      />
                    </Grid>
                  ) : isOrganizacion ? (
                    <Grid item xs={12} md={6}>
                      <GetAsignaturasOrganizacion
                        nivelId={nivelOrg.id}
                        selectedAsignatura={asignaturaOrg.id}
                        onAsignaturaChange={(a) => {
                          setAsignaturaOrg({ id: a.id, nombre: a.nombre });
                          setPromptData((prev: any) => ({
                            ...prev,
                            materia_org: a.nombre,
                            materia_cn: prev.materia_cn || "",
                            materia_tp: ""

                          }));

                          setProyecto((prev: any) => {
                            const prevTp: string[] = Array.isArray(prev?.materias_org) ? prev.materias_org : [];
                            const nombre = a?.nombre ?? "";
                            return {
                              ...prev,
                              materias_cn: prev.materias_cn || [],
                              materias_tp: [],
                              materias_org: nombre && !prevTp.includes(nombre) ? [...prevTp, nombre] : prevTp,
                            };
                          });
                        }}
                      />


                    </Grid>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <GetAsignaturas
                        nivelId={nivelId}
                        onAsignaturaChange={handleAsignaturaChangeLocal}
                        selectedAsignatura={materiaId}
                      />
                    </Grid>
                  )}

                  {/* OBJETIVOS */}
                  <Grid item xs={12} mt={3}>
                    {isTecnicoProfesional ? (
                      <GetObjetivosAprendizajeV2
                        nivelId={nivelTPId}
                        asignaturaId={asignaturaTPId}
                        materiaNombre={promptData.materia_tp}
                        promptData={promptData}
                        setPromptData={setPromptData}
                      />
                    ) : isOrganizacion ? (
                      <GetObjetivosAprendizajeOrganizacion idNivelAsignatura={asignaturaOrg.id} />

                    ) : (
                      <GetObjetivosAprendizaje
                        nombreAsignatura={promptData.materia_cn}
                        nombreNivel={promptData.nivel_cn}
                      />
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default CurriculumTabs;
